import React, { createContext, useContext, useState, useEffect } from 'react';
import { StoreSettings } from '../types/store';
import {
  subscribeStoreSettingsDb,
  saveStoreSettingsDb,
  DEFAULT_STORE_SETTINGS,
} from '../lib/firebase';
import {
  initTikTokPixel,
  initMetaPixel,
  getTikTokPixelStatus,
} from '../lib/analytics';

interface StoreSettingsContextType {
  settings: StoreSettings;
  isLoading: boolean;
  updateSettings: (newSettings: Partial<StoreSettings>) => Promise<void>;
  pixelStatus: {
    isInitialized: boolean;
    isLoaded: boolean;
    activeId: string;
    isEnabled: boolean;
  };
}

const StoreSettingsContext = createContext<StoreSettingsContextType | undefined>(undefined);

export const StoreSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<StoreSettings>(DEFAULT_STORE_SETTINGS);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pixelStatus, setPixelStatus] = useState(getTikTokPixelStatus());

  useEffect(() => {
    // Subscribe to real-time changes in Firestore settings/store document
    const unsubscribe = subscribeStoreSettingsDb((fetchedSettings) => {
      const active = fetchedSettings || DEFAULT_STORE_SETTINGS;
      setSettings(active);
      setIsLoading(false);

      // Dynamically initialize or sync TikTok Pixel
      const ttId = active.tiktokPixelId || DEFAULT_STORE_SETTINGS.tiktokPixelId || 'DA86HQBC77UES9745BGG';
      const ttEnabled = active.tiktokPixelEnabled !== false;
      initTikTokPixel(ttId, ttEnabled);

      // Dynamically initialize or sync Meta Pixel
      const metaId = active.metaPixelId || DEFAULT_STORE_SETTINGS.metaPixelId || '1041026968930278';
      const metaEnabled = active.metaPixelEnabled !== false;
      initMetaPixel(metaId, metaEnabled);

      setPixelStatus(getTikTokPixelStatus());
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const updateSettings = async (newSettings: Partial<StoreSettings>) => {
    const payload: Partial<StoreSettings> = {
      ...newSettings,
      lastUpdated: new Date().toISOString(),
    };
    await saveStoreSettingsDb(payload);
    setSettings((prev) => ({ ...prev, ...payload }));

    if (payload.tiktokPixelId !== undefined || payload.tiktokPixelEnabled !== undefined) {
      const nextId = payload.tiktokPixelId !== undefined ? payload.tiktokPixelId : settings.tiktokPixelId;
      const nextEnabled = payload.tiktokPixelEnabled !== undefined ? payload.tiktokPixelEnabled : settings.tiktokPixelEnabled !== false;
      initTikTokPixel(nextId, nextEnabled);
      setPixelStatus(getTikTokPixelStatus());
    }

    if (payload.metaPixelId !== undefined || payload.metaPixelEnabled !== undefined) {
      const nextId = payload.metaPixelId !== undefined ? payload.metaPixelId : settings.metaPixelId;
      const nextEnabled = payload.metaPixelEnabled !== undefined ? payload.metaPixelEnabled : settings.metaPixelEnabled !== false;
      initMetaPixel(nextId, nextEnabled);
    }
  };

  return (
    <StoreSettingsContext.Provider
      value={{
        settings,
        isLoading,
        updateSettings,
        pixelStatus,
      }}
    >
      {children}
    </StoreSettingsContext.Provider>
  );
};

export const useStoreSettings = (): StoreSettingsContextType => {
  const context = useContext(StoreSettingsContext);
  if (!context) {
    throw new Error('useStoreSettings must be used within a StoreSettingsProvider');
  }
  return context;
};
